import { useState, type FormEvent } from 'react';
import { Button, Close, Modal, TextField } from '../../../modules/design-system';
import {
  createAddress,
  type ApiClient,
  type CreateAddressParams,
  type ShippingAddress,
} from '../../../modules/api';
import { AddressAutocomplete } from './address/AddressAutocomplete';
import {
  EMPTY_ADDRESS_FORM,
  validateAddressForm,
  type AddressFormErrors,
  type AddressFormValues,
} from './address/addressValidation';
import type { ParsedAddress } from './address/parseAddressDetails';

export interface AddressModalProps {
  client: ApiClient;
  onClose: () => void;
  onSuccess: (address: ShippingAddress) => void;
}

/**
 * Add-address form — light card (Figma 710:3939 "Add Address"). Google
 * Places search at the top auto-fills the detail fields, which reveal
 * progressively (web parity); an "enter manually" link reveals them too,
 * so the form works even before the Places key is configured. Built on the
 * DS `TextField` so it reads on the white card (the old dark-card inputs
 * rendered invisible here).
 */
export function AddressModal({ client, onClose, onSuccess }: AddressModalProps) {
  const [form, setForm] = useState<AddressFormValues>(EMPTY_ADDRESS_FORM);
  const [showFields, setShowFields] = useState(false);
  const [errors, setErrors] = useState<AddressFormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set =
    (key: keyof AddressFormValues) =>
    (value: string) =>
      setForm((s) => ({ ...s, [key]: value }));

  const handleResolved = (parsed: ParsedAddress) => {
    setForm((s) => ({
      ...s,
      line1: parsed.line1,
      line2: parsed.line2,
      city: parsed.city,
      state: parsed.state,
      country: parsed.country,
      postalCode: parsed.postalCode,
    }));
    setShowFields(true);
    setErrors({});
  };

  const canSubmit =
    form.name.trim() !== '' &&
    form.phone.trim() !== '' &&
    form.line1.trim() !== '' &&
    form.city.trim() !== '' &&
    form.postalCode.trim() !== '' &&
    form.country.trim() !== '';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    const found = validateAddressForm(form, true);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      if (!showFields) setShowFields(true);
      return;
    }
    setErrors({});
    setSubmitting(true);
    setError(null);
    try {
      const line2 = form.line2.trim();
      const state = form.state.trim();
      const additional = form.additional.trim();
      const params: CreateAddressParams = {
        name: form.name.trim(),
        phoneNumber: form.phone.trim(),
        firstLine: form.line1.trim(),
        city: form.city.trim(),
        zipcode: form.postalCode.trim(),
        country: form.country.trim(),
        ...(line2 ? { secondLine: line2 } : {}),
        ...(state ? { state } : {}),
        ...(additional ? { additional } : {}),
      };
      const saved = await createAddress(client, params);
      onSuccess(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save address');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} inspectName="AddressModal" ariaLabelledBy="address-modal-title">
      <form
        onSubmit={handleSubmit}
        data-co-component="AddressModal"
        className="relative flex max-h-[88vh] flex-col rounded-[28px] p-6"
        style={{
          background: 'var(--co-AddressModal-cardFill, #FFFFFF)',
          color: 'var(--co-AddressModal-textColor, #15181E)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full transition-opacity hover:opacity-80 active:scale-95"
          style={{
            background: 'var(--co-AddressModal-closeFill, rgba(0,0,0,0.05))',
            color: 'var(--co-AddressModal-closeIconColor, #15181E)',
          }}
        >
          <Close size={18} strokeWidth={2} />
        </button>

        <h2
          id="address-modal-title"
          className="mb-5 text-[20px] font-bold tracking-[-0.3px]"
        >
          Add Address
        </h2>

        <div className="flex flex-col gap-3 overflow-y-auto pr-0.5">
          <TextField
            label="Full Name"
            value={form.name}
            onChange={set('name')}
            placeholder="John Doe"
            {...(errors.name ? { error: errors.name } : {})}
          />
          <TextField
            label="Phone"
            value={form.phone}
            onChange={set('phone')}
            placeholder="+1 234 567 8900"
            inputMode="tel"
            {...(errors.phone ? { error: errors.phone } : {})}
          />

          <AddressAutocomplete
            value={form.addressSearch}
            onChange={set('addressSearch')}
            onResolved={handleResolved}
          />

          {!showFields && (
            <button
              type="button"
              onClick={() => setShowFields(true)}
              className="self-start text-[13px] underline underline-offset-2"
              style={{ color: 'var(--co-AddressModal-linkColor, rgba(21,24,30,0.6))' }}
            >
              Enter address manually
            </button>
          )}

          {showFields && (
            <>
              <TextField
                label="Address line 1"
                value={form.line1}
                onChange={set('line1')}
                placeholder="123 Main St"
                {...(errors.line1 ? { error: errors.line1 } : {})}
              />
              <TextField
                label="Address line 2"
                value={form.line2}
                onChange={set('line2')}
                placeholder="Apt, suite, unit (optional)"
              />
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  label="City"
                  value={form.city}
                  onChange={set('city')}
                  placeholder="New York"
                  {...(errors.city ? { error: errors.city } : {})}
                />
                <TextField
                  label="State / Region"
                  value={form.state}
                  onChange={set('state')}
                  placeholder="NY"
                />
                <TextField
                  label="Postal code"
                  value={form.postalCode}
                  onChange={set('postalCode')}
                  placeholder="10001"
                  inputMode="numeric"
                  {...(errors.postalCode ? { error: errors.postalCode } : {})}
                />
                <TextField
                  label="Country"
                  value={form.country}
                  onChange={set('country')}
                  placeholder="US"
                  {...(errors.country ? { error: errors.country } : {})}
                />
              </div>
              <TextField
                label="Additional information"
                value={form.additional}
                onChange={set('additional')}
                placeholder="Delivery instructions, gate code, etc."
              />
            </>
          )}
        </div>

        {error && (
          <p role="alert" className="mt-3 text-[12px]" style={{ color: '#E5484D' }}>
            {error}
          </p>
        )}

        <div className="mt-5">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={!canSubmit}
            loading={submitting}
            fullWidth
          >
            Add Address
          </Button>
        </div>
      </form>
    </Modal>
  );
}
